/**
 * ARAM Platform - Header Navigation
 * Core Desarrollos S.A.S - Copyright 2024-2025
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { useThemeStore } from '@/stores/themeStore'
import { getInitials } from '@/lib/utils'
import {
  Menu,
  Bell,
  Sun,
  Moon,
  Monitor,
  LogOut,
  User,
  Settings,
  ChevronDown,
  BellRing,
  Building2,
  Code2,
  MessageCircle,
  Shield,
  SlidersHorizontal,
  Users,
} from 'lucide-react'
import { LogisticsModeSwitcher } from '@/components/navigation/LogisticsModeSwitcher'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface HeaderProps {
  sidebarOpen: boolean
  onToggleSidebar: () => void
}

export function Header({ sidebarOpen: _sidebarOpen, onToggleSidebar }: HeaderProps) {
  const navigate = useNavigate()
  const { user, logout, hasPermission } = useAuthStore()
  const { unreadCount, fetchUnreadCount } = useNotificationStore()
  const { theme, setTheme } = useThemeStore()

  const handleLogout = () => {
    logout()
    navigate('/auth/login')
  }

  // Cycle: light → dark → system → light
  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light'
    setTheme(next)
  }

  const ThemeIcon = theme === 'dark' ? Sun : theme === 'system' ? Monitor : Moon
  const themeLabel = theme === 'light' ? 'Cambiar a oscuro' : theme === 'dark' ? 'Modo sistema' : 'Cambiar a claro'
  const canManageUsers = hasPermission('users:read')
  const canManageRoles = hasPermission('roles:read')
  const canManageOrganizations = hasPermission('organizations:read')
  const canViewSecurity = hasPermission('security:read')
  const canViewAdministration = Boolean(
    user?.is_superuser ||
    canManageUsers ||
    canManageRoles ||
    canManageOrganizations ||
    canViewSecurity
  )

  useEffect(() => {
    fetchUnreadCount()
    const intervalId = window.setInterval(fetchUnreadCount, 60_000)
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount()
    }
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [fetchUnreadCount])

  return (
    <header className="sticky top-0 z-30 h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex items-center justify-between h-full px-4">
        {/* Left side */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleSidebar}
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>

        {/* Center - Logistics Mode Switcher (admin only) */}
        {user?.is_superuser && (
          <LogisticsModeSwitcher />
        )}

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Theme toggle — cycles light / dark / system */}
          <Button variant="ghost" size="icon" onClick={toggleTheme} title={themeLabel}>
            <ThemeIcon className="h-5 w-5" />
          </Button>

          {/* Notifications */}
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => navigate('/notifications')}
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2">
                <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground text-sm font-medium">
                    {user ? getInitials(`${user.first_name} ${user.last_name}`) : '?'}
                  </span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">
                    {user ? `${user.first_name} ${user.last_name}` : 'Usuario'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
                <ChevronDown className="h-4 w-4 hidden md:block" />
              </Button>
            </DropdownMenuTrigger>
            
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mi cuenta</DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              <DropdownMenuItem onClick={() => navigate('/settings/profile')}>
                <User className="mr-2 h-4 w-4" />
                Perfil
              </DropdownMenuItem>

              <DropdownMenuItem onClick={() => navigate('/settings/notifications')}>
                <BellRing className="mr-2 h-4 w-4" />
                Preferencias
              </DropdownMenuItem>

              {hasPermission('communications.view') && (
                <DropdownMenuItem onClick={() => navigate('/communications')}>
                  <MessageCircle className="mr-2 h-4 w-4" />
                  ARAM Connect
                </DropdownMenuItem>
              )}

              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                Configuración
              </DropdownMenuItem>

              {canViewAdministration && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Shield className="mr-2 h-4 w-4" />
                    Administración
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-56">
                    {user?.is_superuser && (
                      <DropdownMenuItem onClick={() => navigate('/settings/vertical')}>
                        <SlidersHorizontal className="mr-2 h-4 w-4" />
                        Vertical del tenant
                      </DropdownMenuItem>
                    )}
                    {canManageUsers && (
                      <DropdownMenuItem onClick={() => navigate('/settings/users')}>
                        <Users className="mr-2 h-4 w-4" />
                        Usuarios
                      </DropdownMenuItem>
                    )}
                    {canManageRoles && (
                      <DropdownMenuItem onClick={() => navigate('/settings/roles')}>
                        <Shield className="mr-2 h-4 w-4" />
                        Roles y permisos
                      </DropdownMenuItem>
                    )}
                    {canManageOrganizations && (
                      <DropdownMenuItem onClick={() => navigate('/settings/organizations')}>
                        <Building2 className="mr-2 h-4 w-4" />
                        Organizaciones
                      </DropdownMenuItem>
                    )}
                    {canViewSecurity && (
                      <DropdownMenuItem onClick={() => navigate('/settings/security')}>
                        <Shield className="mr-2 h-4 w-4" />
                        Seguridad
                      </DropdownMenuItem>
                    )}
                    {user?.is_superuser && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/settings/code-intelligence')}>
                          <Code2 className="mr-2 h-4 w-4" />
                          Herramientas técnicas
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}

              <DropdownMenuSeparator />
              
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
